/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit,
  output,
  ViewChild,
  ElementRef,
  computed,
  effect,
  signal,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter, Subject, takeUntil } from 'rxjs';
import { TabsComponent, TabItem as NebulaTabItem, NebulaNavigationService } from 'nebula-ui-kit';
import { BreadcrumbSyncService } from '@nebula-layout/layout';
import { LucideAngularModule } from 'lucide-angular';
import { ModuleKey } from '@nebula-layout/layout';
import { RouteSyncService } from '@nebula-layout/layout';
import { SidebarMenuItem } from '@nebula-layout/layout';
import {
  VaultMenuAccessService,
  VaultSidebarMenuService,
  VaultAppToastService,
  VAULT_DEFAULT_POST_LOGIN_ROUTE,
  consumeVaultPostLoginReturnUrl,
  consumeVaultTabsSnapshotForExpiryLogin,
  notifyVaultSessionRestored,
  readVaultLogoutReason,
  type VaultTabsSnapshot,
  type VaultTabsSnapshotTab,
} from '@nebula/shared-vault/data-access';
import { applyVaultMenuLabels } from '../../../../utils/vault-menu-labels';
import { ensurePreservationMessagesSidebarItem } from '../../../../utils/preservation-messages-menu.util';
import { filterVaultSidebarMenuItems } from '../../../../utils/vault-sidebar-hidden-menu.util';
import { buildBreadcrumbItemsFromRouter } from '../../../../utils/breadcrumb-from-router.util';
import { VAULT_MAX_OPEN_TABS } from '../../../../../const/src/lib/layout.constants';

export interface TabContainerConfig {
  moduleKey: ModuleKey;
  moduleLabel: string;
  enableBreadcrumb?: boolean;
  enableUrlSync?: boolean;
}

export interface AppTabItem extends NebulaTabItem {
  module?: 'accounting' | 'tesorería' | 'budget' | 'vault';
  createdAt?: number | string | Date;
  isBaseTab?: boolean;
}

type TabChangeEvent = string | number | { id: string | number };
type TabCloseEvent = string | number | { id: string | number };

@Component({
  selector: 'app-tab-container-wrapper',
  standalone: true,
  imports: [CommonModule, TabsComponent, LucideAngularModule, RouterOutlet],
  templateUrl: './tab-container-wrapper.component.html',
  styleUrls: ['./tab-container-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabContainerWrapperComponent implements OnInit, OnDestroy, AfterViewInit {
  private static readonly VAULT_NON_INGEST_SECTIONS = new Set([
    'dashboard',
    'preservation',
    'retention',
    'legal-hold',
    'reports',
    'cloud',
    'notifications',
    'audit',
    'settings',
    'admin',
    'preservation-messages',
    'repository-admin',
  ]);
  readonly config = input.required<TabContainerConfig>();
  readonly showClose = input<boolean>(true);
  readonly contentMinHeight = input<string>('400px');
  readonly emptyMessage = input<string>('Selecciona una opción del menú para comenzar');
  readonly emptyPadding = input<string>('60px 40px');

  readonly tabActivated = output<AppTabItem>();
  readonly tabClosed = output<string>();

  @ViewChild('tabsHeader') tabsHeaderRef!: ElementRef<HTMLElement>;
  @ViewChild('tabsMenuBtn') tabsMenuBtnRef?: ElementRef<HTMLButtonElement>;

  readonly navService = inject(NebulaNavigationService);
  readonly router = inject(Router);
  readonly cdr = inject(ChangeDetectorRef);
  readonly breadcrumbSync = inject(BreadcrumbSyncService);
  readonly routeSync = inject(RouteSyncService);
  readonly vaultSidebarMenu = inject(VaultSidebarMenuService);
  readonly menuAccess = inject(VaultMenuAccessService);
  readonly vaultAppToast = inject(VaultAppToastService);
  readonly destroy$ = new Subject<void>();
  private isInitialized = false;
  private sidebarMenuItems: SidebarMenuItem[] = [];

  private currentModule: ModuleKey | null = null;

  private isUpdatingFromUrl = false;
  private resizeObserver: ResizeObserver | null = null;
  private tabsMutationObserver: MutationObserver | null = null;
  private scrollTimeout: ReturnType<typeof setTimeout> | null = null;
  private scrollSyncFrame: number | null = null;
  private scrollAlignFrame: number | null = null;
  private scrollAlignTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastScrollAlignedActiveId = '';

  protected hasScrollableContent = false;

  protected readonly tabs = this.navService.tabs;
  protected readonly activeId = this.navService.activeTabId;

  private lastFilteredTabsHash = '';
  private cachedFilteredTabs: AppTabItem[] = [];

  protected readonly filteredTabs = computed(() => {
    const currentTabs = this.tabs();
    const currentConfig = this.config();

    const hash = `${currentTabs.length}-${currentConfig.moduleKey}-${JSON.stringify(currentTabs.map((t) => t.id))}`;

    if (this.lastFilteredTabsHash !== hash) {
      this.lastFilteredTabsHash = hash;
      this.cachedFilteredTabs = this.getFilteredTabs();
    }

    return this.cachedFilteredTabs;
  });

  constructor() {
    effect(() => {
      const tabs = this.filteredTabs();
      const count = tabs.length;
      const active = this.getIdAsString(this.activeId() as string | number | undefined);
      if (count === 0) {
        this.lastScrollAlignedActiveId = '';
        return;
      }
      void tabs;
      this.scheduleTabsScrollSync();
      if (active && active !== this.lastScrollAlignedActiveId) {
        this.lastScrollAlignedActiveId = active;
        this.scheduleScrollToActiveTab();
      }
    });
  }

  protected getIdAsString(id: string | number | undefined): string {
    return id == null ? '' : String(id);
  }

  private _normalizeId(id: string | number | undefined): string | undefined {
    return id == null ? undefined : String(id);
  }

  protected _getIdSafe(tab: AppTabItem): string {
    const id = this._normalizeId(tab.id);
    return id || '';
  }

  private _toServiceKey(key: ModuleKey): ModuleKey {
    return key;
  }

  // ===================== Menú de pestañas Vault (listado + cierre por grupo) =====================

  protected readonly vaultMaxOpenTabs = VAULT_MAX_OPEN_TABS;
  protected readonly vaultTabsMenuOpen = signal(false);
  protected readonly vaultTabsMenuTop = signal(0);
  protected readonly vaultTabsMenuRight = signal(8);
  protected readonly vaultClosableTabs = computed(() =>
    this.filteredTabs().filter((t) => !t.isBaseTab),
  );

  private positionVaultTabsMenu(): void {
    const btn = this.tabsMenuBtnRef?.nativeElement;
    if (!btn || typeof window === 'undefined') return;
    const r = btn.getBoundingClientRect();
    this.vaultTabsMenuTop.set(Math.round(r.bottom + 6));
    this.vaultTabsMenuRight.set(Math.max(8, Math.round(window.innerWidth - r.right)));
  }

  protected toggleVaultTabsMenu(): void {
    const willOpen = !this.vaultTabsMenuOpen();
    if (willOpen) this.positionVaultTabsMenu();
    this.vaultTabsMenuOpen.set(willOpen);
  }

  protected closeVaultTabsMenu(): void {
    this.vaultTabsMenuOpen.set(false);
  }

  private vaultActiveTabIndex(): number {
    const active = String(this.activeId() ?? '');
    return this.filteredTabs().findIndex((t) => this._getIdSafe(t) === active);
  }

  protected hasVaultOtherTabs(): boolean {
    const active = String(this.activeId() ?? '');
    return this.vaultClosableTabs().some((t) => this._getIdSafe(t) !== active);
  }

  protected hasVaultTabsToLeft(): boolean {
    const ai = this.vaultActiveTabIndex();
    return (
      ai > 0 &&
      this.filteredTabs()
        .slice(0, ai)
        .some((t) => !t.isBaseTab)
    );
  }

  protected hasVaultTabsToRight(): boolean {
    const ai = this.vaultActiveTabIndex();
    const tabs = this.filteredTabs();
    return ai >= 0 && tabs.slice(ai + 1).some((t) => !t.isBaseTab);
  }

  protected selectVaultTabFromMenu(tab: AppTabItem): void {
    this.closeVaultTabsMenu();
    this.onTabChange(this._getIdSafe(tab));
  }

  protected closeVaultTabFromMenu(tab: AppTabItem, event: Event): void {
    event.stopPropagation();
    this.onClose(this._getIdSafe(tab));
  }

  protected closeVaultOtherTabs(): void {
    const active = String(this.activeId() ?? '');
    const ids = this.filteredTabs()
      .filter((t) => !t.isBaseTab && this._getIdSafe(t) !== active)
      .map((t) => this._getIdSafe(t));
    this.bulkCloseVaultInactiveTabs(ids);
    this.closeVaultTabsMenu();
  }

  protected closeVaultTabsToLeft(): void {
    const ai = this.vaultActiveTabIndex();
    if (ai < 0) return;
    const ids = this.filteredTabs()
      .slice(0, ai)
      .filter((t) => !t.isBaseTab)
      .map((t) => this._getIdSafe(t));
    this.bulkCloseVaultInactiveTabs(ids);
    this.closeVaultTabsMenu();
  }

  protected closeVaultTabsToRight(): void {
    const ai = this.vaultActiveTabIndex();
    if (ai < 0) return;
    const ids = this.filteredTabs()
      .slice(ai + 1)
      .filter((t) => !t.isBaseTab)
      .map((t) => this._getIdSafe(t));
    this.bulkCloseVaultInactiveTabs(ids);
    this.closeVaultTabsMenu();
  }

  protected closeAllVaultTabs(): void {
    const active = String(this.activeId() ?? '');
    const others = this.filteredTabs()
      .filter((t) => !t.isBaseTab && this._getIdSafe(t) !== active)
      .map((t) => this._getIdSafe(t));
    others.forEach((id) => this.navService.closeTab(id));

    const activeTab = this.filteredTabs().find((t) => this._getIdSafe(t) === active);
    if (activeTab && !activeTab.isBaseTab) {
      this.onClose(active);
    } else {
      requestAnimationFrame(() => {
        this.updateScrollState();
        this.cdr.markForCheck();
      });
    }
    this.closeVaultTabsMenu();
  }

  private bulkCloseVaultInactiveTabs(ids: string[]): void {
    ids.forEach((id) => {
      this.navService.closeTab(id);
      this.tabClosed.emit(id);
    });
    requestAnimationFrame(() => {
      this.updateScrollState();
      this.scrollToActiveTab();
      this.cdr.markForCheck();
    });
  }

  private updateVaultDocumentTitle(): void {
    if (typeof document === 'undefined') return;
    const base = 'Nebula Vault';
    const activeId = this.activeId();
    const tab = this.filteredTabs().find((t) => this._getIdSafe(t) === String(activeId));
    document.title = tab?.title ? `${tab.title} - ${base}` : base;
  }

  private updateScrollState(): void {
    if (!this.tabsHeaderRef) return;
    const container = this.tabsHeaderRef.nativeElement;
    const scrollThreshold = 1;
    const tabsContent = container.querySelector('.tabs-container, lib-tabs') as HTMLElement | null;
    const contentWidth = Math.max(tabsContent?.scrollWidth ?? 0, container.scrollWidth);
    const viewWidth = container.clientWidth;
    this.hasScrollableContent = contentWidth > viewWidth + scrollThreshold;
    this.cdr.markForCheck();
  }

  private scheduleTabsScrollSync(): void {
    if (this.scrollSyncFrame != null) {
      cancelAnimationFrame(this.scrollSyncFrame);
    }
    this.scrollSyncFrame = requestAnimationFrame(() => {
      this.scrollSyncFrame = requestAnimationFrame(() => {
        this.scrollSyncFrame = null;
        this.updateScrollState();
      });
    });
  }

  private scheduleScrollToActiveTab(attempt = 0): void {
    if (this.scrollAlignTimeout) {
      clearTimeout(this.scrollAlignTimeout);
      this.scrollAlignTimeout = null;
    }
    if (this.scrollAlignFrame != null) {
      cancelAnimationFrame(this.scrollAlignFrame);
    }
    this.scrollAlignFrame = requestAnimationFrame(() => {
      this.scrollAlignFrame = requestAnimationFrame(() => {
        this.scrollAlignFrame = null;
        const aligned = this.scrollToActiveTab();
        if (!aligned && attempt < 10) {
          this.scrollAlignTimeout = setTimeout(
            () => this.scheduleScrollToActiveTab(attempt + 1),
            40,
          );
        }
      });
    });
  }

  /** Centra la pestaña activa en la barra horizontal. */
  private scrollToActiveTab(): boolean {
    if (!this.tabsHeaderRef) return false;
    const container = this.tabsHeaderRef.nativeElement;
    this.updateScrollState();
    if (!this.hasScrollableContent) return true;

    const activeTab = container.querySelector(
      'button.tab-item.active, button.tab-item[aria-selected="true"]',
    ) as HTMLElement | null;
    if (!activeTab) return false;

    const tabsContent = container.querySelector('.tabs-container, lib-tabs') as HTMLElement | null;
    const contentWidth = Math.max(tabsContent?.scrollWidth ?? 0, container.scrollWidth);
    const maxScroll = Math.max(0, contentWidth - container.clientWidth);
    const tabLeft = activeTab.offsetLeft;
    const tabWidth = activeTab.offsetWidth;
    const tabCenter = tabLeft + tabWidth / 2;
    const targetScroll = Math.max(
      0,
      Math.min(maxScroll, tabCenter - container.clientWidth / 2),
    );

    container.scrollTo({ left: targetScroll, behavior: 'smooth' });
    if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => this.updateScrollState(), 120);
    return true;
  }

  private setupScrollListeners(): void {
    if (!this.tabsHeaderRef) return;
    const container = this.tabsHeaderRef.nativeElement;
    const updateScroll = () => this.scheduleTabsScrollSync();
    container.addEventListener('scroll', updateScroll, { passive: true });
    container.addEventListener(
      'wheel',
      (e: WheelEvent) => {
        if (
          container.scrollWidth > container.clientWidth + 1 &&
          Math.abs(e.deltaX) < Math.abs(e.deltaY)
        ) {
          e.preventDefault();
          container.scrollLeft += e.deltaY;
          updateScroll();
        }
      },
      { passive: false },
    );
    this.resizeObserver = new ResizeObserver(() => this.scheduleTabsScrollSync());
    this.resizeObserver.observe(container, { box: 'border-box' });

    const tabsRoot =
      container.querySelector('.tabs-container, .lib-tabs-container, .tabs-list, lib-tabs') ??
      container;
    this.tabsMutationObserver = new MutationObserver(() => this.scheduleTabsScrollSync());
    this.tabsMutationObserver.observe(tabsRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-selected'],
    });

    this.scheduleTabsScrollSync();
  }

  protected getFilteredTabs = (): AppTabItem[] => {
    const cfg = this.config();
    const moduleKey = this._toServiceKey(cfg.moduleKey);

    const tabsWithModule = this.tabs().map((tab) => ({
      ...tab,
      module: this._detectModuleFromRoute(tab.route),
      isBaseTab: this._isBaseRoute(tab.route, moduleKey),
      closable: this._isBaseRoute(tab.route, moduleKey) ? false : (tab.closable ?? true),
    }));

    const moduleTabs = tabsWithModule.filter((tab) => {
      if (moduleKey === 'budget') return tab.module === 'budget';
      if (moduleKey === 'tesorería') return tab.module === 'tesorería';
      if (moduleKey === 'vault') return tab.module === 'vault';
      return tab.module === 'accounting';
    });

    const seen = new Set<string>();
    const uniqueTabs: AppTabItem[] = [];

    moduleTabs.forEach((tab) => {
      const key = (tab.route || '')
        .toLowerCase()
        .trim()
        .replace(/tesoreria/g, 'tesorería')
        .split('?')[0]
        .split('#')[0];
      if (!seen.has(key)) {
        seen.add(key);
        uniqueTabs.push(tab);
      }
    });

    return uniqueTabs.sort((a: AppTabItem, b: AppTabItem): number => {
      if (a.isBaseTab && !b.isBaseTab) return -1;
      if (!a.isBaseTab && b.isBaseTab) return 1;

      const timeA =
        typeof a.createdAt === 'number' ? a.createdAt : new Date(a.createdAt ?? 0).getTime();
      const timeB =
        typeof b.createdAt === 'number' ? b.createdAt : new Date(b.createdAt ?? 0).getTime();

      return timeA - timeB;
    });
  };

  private _isBaseRoute(route: string | null | undefined, module: string): boolean {
    if (!route) return false;
    const n = route.toLowerCase().replace(/tesoreria/g, 'tesorería');
    return n === `/${module}` || n === `/layout/${module}`;
  }

  private _detectModuleFromRoute(
    route: string | null | undefined,
  ): 'accounting' | 'tesorería' | 'budget' | 'vault' {
    if (!route) return 'accounting';
    const n = route.toLowerCase().replace(/tesoreria/g, 'tesorería');
    if (n.includes('budget') || n.includes('presupuesto')) return 'budget';
    if (n.includes('tesorer') || n.includes('treasury')) return 'tesorería';
    if (n.includes('vault')) return 'vault';

    return 'accounting';
  }

  ngOnInit(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;
    this.loadSidebarMenuForParentIcons();

    this.currentModule = this._toServiceKey(this.config().moduleKey);

    const currentUrl = this.router.url;

    if (this.currentModule === 'vault') {
      const wasExpiryRestore = readVaultLogoutReason() === 'expiry';
      const postLoginRoute = consumeVaultPostLoginReturnUrl();
      const tabsSnapshot = consumeVaultTabsSnapshotForExpiryLogin();
      if (postLoginRoute || tabsSnapshot?.tabs?.length) {
        this._restoreVaultSessionAfterExpiryLogout(
          postLoginRoute,
          tabsSnapshot,
          wasExpiryRestore,
        );
        return;
      }
      this._ensureVaultDefaultHomeRouteIfNeeded();
    }

    let activeModuleFromUrl: string | null = null;

    if (currentUrl.includes('/layout/vault')) {
      activeModuleFromUrl = 'vault';
    } else if (currentUrl.includes('/layout/accounting')) {
      activeModuleFromUrl = 'accounting';
    } else if (currentUrl.includes('/layout/tesorería')) {
      activeModuleFromUrl = 'tesorería';
    } else if (currentUrl.includes('/layout/budget')) {
      activeModuleFromUrl = 'budget';
    }

    if (activeModuleFromUrl && activeModuleFromUrl !== this.currentModule) {
      this.isInitialized = true;
      return;
    }

    const hasTabs = this.navService.tabs().length > 0;

    if (this.config().enableUrlSync) {
      const savedRoute = this.routeSync.getRoute(this.currentModule);
      const rootRoute = `/layout/${this.currentModule}`;

      if (hasTabs && savedRoute && savedRoute !== currentUrl && currentUrl === rootRoute) {
        setTimeout(() => {
          this.router
            .navigateByUrl(savedRoute, {
              replaceUrl: true,
              skipLocationChange: false,
            })
            .then(() => {
              this._syncActiveTabWithUrl();
            });
        }, 0);
      } else if (hasTabs) {
        this._syncActiveTabWithUrl();
      } else if (!hasTabs && savedRoute) {
        this.routeSync.clearRoute(this.currentModule);
      }
    }

    this._syncActiveIdWithModule();
    this._activateFirstTabIfNeeded();
    this._updateBreadcrumbForActiveTab();
    this.cdr.markForCheck();
  }

  private _ensureVaultDefaultHomeRouteIfNeeded(): void {
    const homeRoute = VAULT_DEFAULT_POST_LOGIN_ROUTE;
    const normalizedHome = this.routeSync.normalizeRoute(homeRoute);
    const normalizedUrl = this.routeSync.normalizeRoute(this.router.url);
    const normalizedRoot = this.routeSync.normalizeRoute('/layout/vault');

    const shouldOpenHome =
      normalizedUrl === normalizedHome ||
      normalizedUrl === normalizedRoot ||
      (this.navService.tabs().length === 0 && normalizedUrl.includes('/layout/vault'));

    if (!shouldOpenHome) return;

    const existingHomeTab = this.navService.tabs().find(
      (tab) => this.routeSync.normalizeRoute(tab.route || '') === normalizedHome,
    );

    if (!existingHomeTab) {
      this.navService.addTabFromMenu({
        label: 'Inicio',
        route: homeRoute,
        icon: 'House',
      });
      return;
    }

    if (normalizedUrl === normalizedRoot) {
      void this.router.navigateByUrl(homeRoute, { replaceUrl: true });
      return;
    }

    if (!this.activeId()) {
      this.navService.selectTab(existingHomeTab.id ?? homeRoute);
    }
  }

  private _restoreVaultSessionAfterExpiryLogout(
    postLoginRoute: string | null,
    tabsSnapshot: VaultTabsSnapshot | null,
    showRestoredToast: boolean,
  ): void {
    const snapshotTabs = tabsSnapshot?.tabs ?? [];
    const targetRoute =
      postLoginRoute ??
      snapshotTabs.find((tab) => tab.id === tabsSnapshot?.activeId)?.route ??
      snapshotTabs[snapshotTabs.length - 1]?.route ??
      VAULT_DEFAULT_POST_LOGIN_ROUTE;

    const normalizedTarget = this.routeSync.normalizeRoute(targetRoute);
    const activeId =
      snapshotTabs.find(
        (tab) => this.routeSync.normalizeRoute(tab.route) === normalizedTarget,
      )?.id ??
      tabsSnapshot?.activeId ??
      targetRoute;

    const tabsToRestore: VaultTabsSnapshotTab[] =
      snapshotTabs.length > 0
        ? snapshotTabs
        : [
            {
              id: targetRoute,
              title: this.menuAccess.getLabelForRoute(targetRoute),
              route: targetRoute,
              icon: 'File',
            },
          ];

    this.isUpdatingFromUrl = true;
    this._applyVaultTabsSnapshotSilently(tabsToRestore, activeId);

    queueMicrotask(() => {
      void this.router.navigateByUrl(targetRoute, { replaceUrl: true }).finally(() => {
        this.isUpdatingFromUrl = false;
        this._updateBreadcrumbForActiveTab();
        if (showRestoredToast) {
          notifyVaultSessionRestored(this.vaultAppToast);
        }
        this.cdr.markForCheck();
      });
    });
  }

  /** Restaura pestañas sin navegar por cada una (evita parpadeo tras re-login). */
  private _applyVaultTabsSnapshotSilently(
    tabs: VaultTabsSnapshotTab[],
    activeId: string,
  ): void {
    type NavInternals = {
      _tabs: { set: (value: NebulaTabItem[]) => void };
      _activeTabId: { set: (value: string | undefined) => void };
      _currentContextId: { set: (value: string) => void };
      syncSignalsToMap: () => void;
      saveToLocalStorage: () => void;
    };

    const nav = this.navService as unknown as NavInternals;
    const tabItems = tabs.map((tab) => ({
      id: tab.id,
      title: tab.title || this.menuAccess.getLabelForRoute(tab.route),
      route: tab.route,
      icon: tab.icon || 'File',
      closable: true,
    })) as NebulaTabItem[];

    nav._currentContextId.set('layout');
    nav._tabs.set(tabItems);
    nav._activeTabId.set(activeId);
    nav.syncSignalsToMap();
    nav.saveToLocalStorage();
  }

  private loadSidebarMenuForParentIcons(): void {
    this.vaultSidebarMenu
      .getVaultSidebarMenu()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          this.sidebarMenuItems = filterVaultSidebarMenuItems(
            ensurePreservationMessagesSidebarItem(applyVaultMenuLabels(items ?? [])),
          );
        },
      });
  }

  private _syncActiveTabWithUrl(): void {
    const currentUrl = this.routeSync.normalizeRoute(this.router.url);
    const moduleKey = this._toServiceKey(this.config().moduleKey);

    setTimeout(() => {
      const matchingTab = this.filteredTabs().find((tab) => {
        const tabRoute = tab.route?.startsWith('/')
          ? tab.route
          : `/layout/${moduleKey}/${tab.route}`;
        return this.routeSync.normalizeRoute(tabRoute) === currentUrl;
      });

      if (matchingTab?.id && this.activeId() !== matchingTab.id) {
        this.navService.selectTab(matchingTab.id);
        this._updateBreadcrumbForActiveTab();
        this.cdr.markForCheck();
      }
    }, 0);
  }

  ngAfterViewInit(): void {
    this.setupScrollListeners();
    this._setupRouterSync();

    if (this.config().enableUrlSync) {
      setTimeout(() => {
        this._syncActiveTabWithUrl();
        this.cdr.markForCheck();
      }, 100);
    }
  }

  async onTabChange(event: TabChangeEvent): Promise<void> {
    const rawId =
      typeof event === 'string' || typeof event === 'number' ? String(event) : String(event.id);
    const tabId = this._normalizeId(rawId);
    if (!tabId) return;

    const tab = this.filteredTabs().find((t) => this._getIdSafe(t) === tabId) as AppTabItem;
    if (!tab) return;

    const serviceKey = this._toServiceKey(this.config().moduleKey);
    const expectedRoute = tab.route?.startsWith('/')
      ? tab.route
      : `/layout/${serviceKey}/${tab.route}`;

    if (
      serviceKey === 'vault' &&
      expectedRoute.includes('/layout/vault') &&
      !this.menuAccess.isRouteAllowed(expectedRoute)
    ) {
      const label = this.menuAccess.getLabelForRoute(expectedRoute);
      this.menuAccess.notifyAccessDenied(label, expectedRoute);
      return;
    }

    this.navService.selectTab(rawId);

    if (expectedRoute && this.router.url !== expectedRoute) {
      this.isUpdatingFromUrl = true;
      try {
        await this.router.navigateByUrl(expectedRoute, {
          replaceUrl: true,
          skipLocationChange: false,
        });
      } finally {
        this.isUpdatingFromUrl = false;
      }
    }

    this._updateBreadcrumbForActiveTab();
    this.cdr.markForCheck();

    this.tabActivated.emit(tab);

    if (expectedRoute && this.config().enableUrlSync) {
      this.routeSync.saveRoute(this.currentModule!, expectedRoute);
    }
  }

  private _setupRouterSync(): void {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((e) => e instanceof NavigationEnd),
      )
      .subscribe(() => {
        if (this.isUpdatingFromUrl) return;

        const normalizedCurrentUrl = this.routeSync.normalizeRoute(this.router.url);
        const moduleKey = this._toServiceKey(this.config().moduleKey);

        if (!normalizedCurrentUrl.includes(`/layout/${moduleKey}`)) return;
        if (moduleKey === 'vault') {
          this._enforceSingleIngestTab(normalizedCurrentUrl);
        }

        setTimeout(() => {
          const activeTab = this.filteredTabs().find(
            (t) => this.routeSync.normalizeRoute(t.route || '') === normalizedCurrentUrl,
          );

          if (activeTab?.id) {
            if (this.activeId() !== activeTab.id) {
              this.navService.selectTab(activeTab.id);
              this.cdr.markForCheck();
            }
            if (this.config().enableUrlSync) {
              this.routeSync.saveRoute(moduleKey, normalizedCurrentUrl);
            }
            this._updateBreadcrumbForActiveTab();
          }
        }, 50);
      });
  }

  private _enforceSingleIngestTab(currentUrlNormalized: string): void {
    if (!this._isIngestRouteKey(currentUrlNormalized)) return;

    const ingestTabs = this.navService.tabs().filter((t) => {
      const tabRoute = this.routeSync.normalizeRoute(t.route || '');
      return this._isIngestRouteKey(tabRoute);
    });
    if (ingestTabs.length <= 1) return;

    ingestTabs.forEach((tab) => {
      const tabRoute = this.routeSync.normalizeRoute(tab.route || '');
      if (tabRoute !== currentUrlNormalized && tab.id != null) {
        this.navService.closeTab(String(tab.id));
      }
    });
  }

  private _isIngestRouteKey(routeKey: string): boolean {
    if (!routeKey) return false;
    if (routeKey.startsWith('/layout/vault/vault/')) return true;
    if (!routeKey.startsWith('/layout/vault/')) return false;
    const rest = routeKey.replace('/layout/vault/', '');
    if (!rest) return false;
    const first = rest.split('/')[0];
    return !TabContainerWrapperComponent.VAULT_NON_INGEST_SECTIONS.has(first);
  }

  onClose(event: TabCloseEvent): void {
    const rawId =
      typeof event === 'string' || typeof event === 'number' ? String(event) : String(event.id);
    const tab = this.filteredTabs().find((t) => this._getIdSafe(t) === this._normalizeId(rawId));
    if (!tab) return;
    const wasActive = this.activeId() === rawId;
    const currentModuleBackup = this.currentModule || this.config().moduleKey;

    let tabToActivate: AppTabItem | null = null;

    if (wasActive) {
      const currentTabs = this.filteredTabs();
      const currentIndex = currentTabs.findIndex(
        (t) => this._getIdSafe(t) === this._normalizeId(rawId),
      );
      if (currentTabs.length > 1) {
        const nextIndex =
          currentIndex < currentTabs.length - 1 ? currentIndex + 1 : currentIndex - 1;
        tabToActivate = currentTabs[nextIndex];
      }
    }

    this.navService.closeTab(rawId);
    this.tabClosed.emit(String(rawId));

    if (wasActive && tabToActivate) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const newRoute = tabToActivate!.route?.startsWith('/')
            ? tabToActivate!.route
            : `/layout/${currentModuleBackup}/${tabToActivate!.route}`;

          if (tabToActivate!.id) {
            this.navService.selectTab(tabToActivate!.id);
          }

          this._updateBreadcrumbForTab(tabToActivate!, currentModuleBackup);

          if (newRoute && this.router.url !== newRoute) {
            this.isUpdatingFromUrl = true;
            const decodedRoute = decodeURIComponent(newRoute);

            this.router
              .navigateByUrl(decodedRoute, { replaceUrl: true })
              .then(() => {
                if (this.config().enableUrlSync) {
                  this.routeSync.saveRoute(currentModuleBackup, decodedRoute);
                }
                this.cdr.markForCheck();
              })
              .finally(() => {
                this.isUpdatingFromUrl = false;
              });
          } else if (newRoute && this.config().enableUrlSync) {
            this.routeSync.saveRoute(currentModuleBackup, newRoute);
            this.cdr.markForCheck();
          }
        }, 50);
      });
    } else if (wasActive) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const dashboardRoute = `/layout/${currentModuleBackup}`;

          this.navService.clearTabs();

          if (this.config().enableUrlSync) {
            this.routeSync.clearRoute(currentModuleBackup);
          }

          this._updateBreadcrumbForDashboard(currentModuleBackup);

          if (this.router.url !== dashboardRoute) {
            this.isUpdatingFromUrl = true;
            const decodedRoute = decodeURIComponent(dashboardRoute);
            this.router
              .navigateByUrl(decodedRoute, { replaceUrl: true })
              .then(() => {
                this.cdr.markForCheck();
              })
              .finally(() => {
                this.isUpdatingFromUrl = false;
              });
          }
        }, 50);
      });
    }

    this.scheduleTabsScrollSync();
  }

  private _updateBreadcrumbForTab(tab: AppTabItem, moduleKey: ModuleKey): void {
    const cfg = this.config();
    if (!cfg.enableBreadcrumb) return;

    const fullRoute = tab.route?.startsWith('/')
      ? tab.route
      : `/layout/${moduleKey}/${tab.route?.replace(/^\//, '')}`;

    this._updateBreadcrumbFromFullRoute(fullRoute);
  }

  private _updateBreadcrumbForDashboard(moduleKey: ModuleKey): void {
    const cfg = this.config();
    if (!cfg.enableBreadcrumb) return;

    const dashboardRoute = `/layout/${moduleKey}`;
    this._updateBreadcrumbFromFullRoute(dashboardRoute);
  }

  private _updateBreadcrumbForActiveTab(): void {
    this.updateVaultDocumentTitle();
    const cfg = this.config();
    if (!cfg.enableBreadcrumb) return;

    const activeId = this.activeId();
    if (!activeId) return;

    const tab = this.filteredTabs().find((t) => this._getIdSafe(t) === String(activeId));
    if (!tab) return;

    const serviceKey = this._toServiceKey(cfg.moduleKey);
    const fullRoute = tab.route?.startsWith('/') ? tab.route : `/layout/${serviceKey}/${tab.route}`;

    this._updateBreadcrumbFromFullRoute(fullRoute);
  }

  private _syncActiveIdWithModule(): void {
    const currentActiveId = this.activeId();
    if (!currentActiveId) return;

    const filteredTabs = this.getFilteredTabs();
    const activeTabExists = filteredTabs.some(
      (t) => this._getIdSafe(t) === String(currentActiveId),
    );

    if (!activeTabExists && filteredTabs.length > 0) {
      const moduleKey = this._toServiceKey(this.config().moduleKey);
      const currentUrl = this.routeSync.normalizeRoute(this.router.url);
      const rootRoute = this.routeSync.normalizeRoute(`/layout/${moduleKey}`);
      const matchingTab = filteredTabs.find((t) => {
        const tabRoute = t.route?.startsWith('/') ? t.route : `/layout/${moduleKey}/${t.route}`;
        return this.routeSync.normalizeRoute(tabRoute || '') === currentUrl;
      });

      if (matchingTab?.id) {
        this.navService.selectTab(matchingTab.id);
        this.cdr.markForCheck();
        return;
      }

      // Si estamos en ruta profunda tras F5, no forzar primer tab (projects).
      if (currentUrl !== rootRoute) {
        return;
      }

      this.navService.selectTab(filteredTabs[0].id);
      this.cdr.markForCheck();
    }
  }

  private _activateFirstTabIfNeeded(): void {
    if (this.activeId()) return;
    const tabs = this.getFilteredTabs();
    if (tabs.length === 0) return;

    const moduleKey = this._toServiceKey(this.config().moduleKey);
    const currentUrl = this.routeSync.normalizeRoute(this.router.url);
    const rootRoute = this.routeSync.normalizeRoute(`/layout/${moduleKey}`);

    // En reload, priorizar pestaña que coincide con la URL actual para no forzar "projects".
    const match = tabs.find((t) => {
      const tabRoute = t.route?.startsWith('/') ? t.route : `/layout/${moduleKey}/${t.route}`;
      return this.routeSync.normalizeRoute(tabRoute || '') === currentUrl;
    });

    // Si no hay match y estamos en ruta profunda, no activar fallback.
    const tabToActivate = match ?? (currentUrl === rootRoute ? tabs[0] : null);
    if (tabToActivate?.id) {
      this.navService.selectTab(tabToActivate.id);
      this.cdr.markForCheck();
    }
  }

  private _updateBreadcrumbFromFullRoute(route: string): void {
    const cleanRoute = route.split('?')[0].split('#')[0];
    const dedupedItems = buildBreadcrumbItemsFromRouter(this.router, {
      urlOverride: cleanRoute,
      getIconForMenuLabel: (l) => this._getSidebarParentIcon(l),
      getLastItemIconForPath: (p) => this._getActiveTabIconForRoute(p),
      lastMenuOrTabIcon: 'File',
    });
    if (dedupedItems.length > 0) {
      this.breadcrumbSync.update(dedupedItems);
    }
  }

  private _getSidebarParentIcon(parentLabel: string): string {
    const normalized = parentLabel.trim().toLowerCase();
    const parent = this.sidebarMenuItems.find((m) => m.label.trim().toLowerCase() === normalized);
    return (parent?.icon as string) || 'Folder';
  }

  private _getActiveTabIconForRoute(routePath: string): string | undefined {
    const normalized = this.routeSync.normalizeRoute(routePath);
    const tabs = this.navService.tabs() as AppTabItem[];
    const hit = tabs.find(
      (tab) => tab.route && this.routeSync.normalizeRoute(tab.route) === normalized,
    );
    return hit?.icon as string | undefined;
  }

  ngOnDestroy(): void {
    if (this.config().enableUrlSync && this.currentModule) {
      const hasTabs = this.navService.tabs().length > 0;
      if (!hasTabs) {
        this.routeSync.clearRoute(this.currentModule);
      }
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.tabsMutationObserver) {
      this.tabsMutationObserver.disconnect();
    }
    if (this.scrollSyncFrame != null) {
      cancelAnimationFrame(this.scrollSyncFrame);
    }
    if (this.scrollAlignFrame != null) {
      cancelAnimationFrame(this.scrollAlignFrame);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    if (this.scrollAlignTimeout) {
      clearTimeout(this.scrollAlignTimeout);
    }
    this.destroy$.next();
    this.destroy$.complete();
    this.isInitialized = false;
    this.currentModule = null;
  }
}
